import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box, Text, Button, Spinner,
} from 'grommet';
import { Copy } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import { Seo, AppButton, SimpleModal } from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { TableWrapper } from '@Components/Partial/Product/SharedComponents.jsx';
import { ProductFormInput } from '@Components/Partial/Product/NewSearch';
import {
  fetchNagraMonitoringOperations,
  createNagraMonitoringOperation,
  endNagraMonitoringOperation,
} from '@Actions';

const OPERATION_STATUS_COLORS = {
  active: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
  ended: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
};

const opsHeaderMap = [
  { uid: 'monitoringUuid', display: 'Monitoring UUID', size: '35%' },
  { uid: 'label', display: 'Label', size: '22%' },
  { uid: 'status', display: 'Status', size: '10%' },
  { uid: 'createdAt', display: 'Created', size: '20%' },
  { uid: 'actions', display: 'Actions', size: '13%' },
];

const Section = ({ title, children, page }) => (
  <Box
    direction="column"
    gap="0.75rem"
    pad="1.25rem"
    round="8px"
    border={{ color: page.tableBorder, size: '1px' }}
    background={page.tableBg}
  >
    <Text size="1rem" weight={600} color={page.primaryText}>
      {title}
    </Text>
    {children}
  </Box>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  page: PropTypes.shape({
    tableBorder: PropTypes.string,
    tableBg: PropTypes.string,
    primaryText: PropTypes.string,
  }).isRequired,
};

const ReadOnlyField = ({
  label, value = null, page, mono = false, copyable = false,
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box direction="row" gap="0.5rem" align="start">
      <Text
        size="0.85rem"
        color={page.secondaryText}
        style={{ minWidth: '160px', flexShrink: 0 }}
      >
        {label}
      </Text>
      <Box direction="row" align="center" gap="0.35rem" flex>
        <Text
          size="0.9rem"
          color={page.tableText}
          style={mono ? { fontFamily: 'monospace', wordBreak: 'break-all' } : { wordBreak: 'break-word' }}
        >
          {value ?? '—'}
        </Text>
        {copyable && value && (
          <Button
            plain
            icon={<Copy size="0.85rem" color={page.secondaryText} />}
            onClick={handleCopy}
            title="Copy to clipboard"
            style={{ padding: '2px', flexShrink: 0 }}
          />
        )}
        {copied && <Text size="0.75rem" color="#00a855">Copied</Text>}
      </Box>
    </Box>
  );
};

ReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  page: PropTypes.shape({
    secondaryText: PropTypes.string,
    tableText: PropTypes.string,
  }).isRequired,
  mono: PropTypes.bool,
  copyable: PropTypes.bool,
};

const OperationStatusBadge = ({ status = null, crawlingEndTs = null }) => {
  const isEnded = Boolean(crawlingEndTs);
  const resolvedStatus = isEnded ? 'ended' : (status || 'active');
  const colors = OPERATION_STATUS_COLORS[resolvedStatus] || OPERATION_STATUS_COLORS.ended;
  return (
    <Box
      background={colors.bg}
      border={{ color: colors.border, size: '1px' }}
      round="6px"
      pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
      width="fit-content"
    >
      <Text size="0.8rem" weight={600} color={colors.text}>
        {resolvedStatus}
      </Text>
    </Box>
  );
};

OperationStatusBadge.propTypes = {
  status: PropTypes.string,
  crawlingEndTs: PropTypes.string,
};

const AdminNagraManageOperationsPage = ({
  small,
  location,
  operationsDoc = null,
  loading,
  creating,
  ending,
  fetchOperations,
  createOperation,
  endOperation,
}) => {
  const [label, setLabel] = React.useState('');
  const [copiedUuid, setCopiedUuid] = React.useState(null);
  const [confirmEndUuid, setConfirmEndUuid] = React.useState(null);
  const { theme: { page, button } } = useAppTheme();

  React.useEffect(() => {
    fetchOperations();
  }, []);

  const handleCreate = () => {
    createOperation(label.trim() || undefined);
    setLabel('');
  };

  const handleCopyUuid = (uuid) => {
    navigator.clipboard.writeText(uuid).then(() => {
      setCopiedUuid(uuid);
      setTimeout(() => setCopiedUuid(null), 2000);
    });
  };

  const handleConfirmEnd = () => {
    if (confirmEndUuid) {
      endOperation(confirmEndUuid);
      setConfirmEndUuid(null);
    }
  };

  const monitoringOps = operationsDoc?.monitoringOperations || [];
  const asset = operationsDoc?.asset;

  const renderOpCell = (uid, op, isEndingThis) => {
    const isEnded = Boolean(op.crawlingEndTs) || op.status === 'ended';
    switch (uid) {
      case 'monitoringUuid':
        return (
          <Box direction="row" align="center" gap="0.35rem">
            <Text size="0.78rem" color={page.tableText} style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {op.monitoringUuid}
            </Text>
            <Button
              plain
              icon={<Copy size="0.85rem" color={page.secondaryText} />}
              onClick={() => handleCopyUuid(op.monitoringUuid)}
              title="Copy UUID"
              style={{ padding: '2px', flexShrink: 0 }}
            />
            {copiedUuid === op.monitoringUuid && (
              <Text size="0.75rem" color="#00a855">Copied</Text>
            )}
          </Box>
        );
      case 'label':
        return op.label
          ? <Text size="0.85rem" color={page.tableText}>{op.label}</Text>
          : <Text size="0.85rem" color={page.secondaryText}>—</Text>;
      case 'status':
        return <OperationStatusBadge status={op.status} crawlingEndTs={op.crawlingEndTs} />;
      case 'createdAt':
        return (
          <Text size="0.85rem" color={page.tableText}>
            {op.createdAt ? new Date(op.createdAt).toLocaleString() : '—'}
          </Text>
        );
      case 'actions':
        return isEnded ? (
          <Text size="0.8rem" color={page.secondaryText}>—</Text>
        ) : (
          <AppButton
            level="tableControls"
            label={isEndingThis ? 'Ending...' : 'End Operation'}
            color="#e53e3e"
            onClick={() => setConfirmEndUuid(op.monitoringUuid)}
            disabled={isEndingThis || Boolean(ending)}
          />
        );
      default: return null;
    }
  };

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Manage Assets & Operations',
        subTitle: 'NAGRA Monitoring',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={page.background}
        pad={{ vertical: '1.5rem', horizontal: small ? '1rem' : '2rem' }}
        gap="1rem"
        width="100%"
      >
        {loading && (
          <Box direction="row" gap="0.5rem" align="center" pad="large" justify="center">
            <Spinner size="medium" />
          </Box>
        )}
        {!loading && !operationsDoc && (
          <Box
            pad="1.25rem"
            round="8px"
            border={{ color: page.tableBorder, size: '1px' }}
            background={page.tableBg}
          >
            <Text size="0.9rem" color={page.secondaryText}>
              No NAGRA operation configuration found in the database.
            </Text>
          </Box>
        )}
        {!loading && operationsDoc && (
          <Box direction="column" gap="1rem">
            <Section title="Asset Info" page={page}>
              <ReadOnlyField label="Title" value={asset?.title} page={page} copyable />
              <ReadOnlyField label="Asset UUID" value={asset?.assetUuid} page={page} mono copyable />
              <ReadOnlyField label="Campaign UUID" value={operationsDoc.campaignUuid} page={page} mono copyable />
              <ReadOnlyField label="Collection ID" value={operationsDoc.collectionId} page={page} copyable />
              <ReadOnlyField label="Monitoring Start" value={asset?.monitoringStartTs} page={page} />
              <ReadOnlyField label="Monitoring End" value={asset?.monitoringEndTs} page={page} />
            </Section>
            <Section title="Create New Monitoring Operation" page={page}>
              <Box direction={small ? 'column' : 'row'} gap="0.75rem" align={small ? 'stretch' : 'center'}>
                <Box flex>
                  <ProductFormInput
                    placeholder="Label (optional — e.g. 'F1 Monaco Qualifying Session')"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    disabled={creating}
                  />
                </Box>
                <AppButton
                  overrideHover
                  level="tableControls"
                  label={creating ? 'Creating...' : 'Submit Monitoring Operation'}
                  width="fit-content"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  onClick={handleCreate}
                  disabled={creating}
                />
              </Box>
            </Section>
            <TableWrapper
              small={small}
              header="NAGRA Monitoring Operations"
              hidePagination
            >
              {monitoringOps.length === 0 ? (
                <Box align="center" justify="center" pad="2.5rem">
                  <Text size="0.85rem" color={page.secondaryText}>No monitoring operations yet.</Text>
                </Box>
              ) : (
                <Box fill="horizontal">
                  <Box
                    direction="row"
                    fill="horizontal"
                    border={{ side: 'bottom', color: page.tableBorder, size: '1px' }}
                    background={page.altBg}
                  >
                    {opsHeaderMap.map(({ uid, display, size }) => (
                      <Box key={uid} width={size} pad={{ horizontal: '0.85rem', vertical: '0.6rem' }}>
                        <Text
                          size="0.78rem"
                          weight={600}
                          color={page.secondaryText}
                          style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                        >
                          {display}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                  {[...monitoringOps].reverse().map((op, idx) => {
                    const isEnded = Boolean(op.crawlingEndTs) || op.status === 'ended';
                    const isEndingThis = ending === op.monitoringUuid;
                    return (
                      <Box
                        key={op.monitoringUuid || idx}
                        direction="row"
                        fill="horizontal"
                        align="center"
                        background={isEnded ? page.tableBg : page.background}
                        border={{ side: 'bottom', color: page.tableBorder, size: '1px' }}
                        style={isEnded ? { opacity: 0.6 } : {}}
                      >
                        {opsHeaderMap.map(({ uid, size }) => (
                          <Box key={uid} width={size} pad={{ horizontal: '0.85rem', vertical: '0.65rem' }}>
                            {renderOpCell(uid, op, isEndingThis)}
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </TableWrapper>
          </Box>
        )}
      </Box>
      {confirmEndUuid && (
        <SimpleModal
          small={small}
          toggleModal={() => setConfirmEndUuid(null)}
          modalTitle="End Monitoring Operation"
          textColor={page.modalText}
          highlightColor={button.primaryBg}
          modalBg={page.modalBg}
          titleSize="1.1rem"
          desktopMinWidth="32rem"
          hideLowerSection
        >
          <Box direction="column" gap="1rem" pad={{ vertical: '0.5rem' }}>
            <Text size="0.95rem" color={page.modalText}>
              Are you sure you want to end monitoring operation
              {' '}
              <Text
                size="0.95rem"
                weight={600}
                color={page.modalText}
                style={{ fontFamily: 'monospace' }}
              >
                {confirmEndUuid}
              </Text>
              ? This cannot be undone.
            </Text>
            <Box direction="row" gap="0.75rem" justify="end">
              <AppButton
                level="tableControls"
                label="Cancel"
                color={page.secondaryText}
                onClick={() => setConfirmEndUuid(null)}
              />
              <AppButton
                level="tableControls"
                label="End Operation"
                color="#e53e3e"
                bgColor="#e53e3e22"
                onClick={handleConfirmEnd}
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
    </AdminNavWrapper>
  );
};

AdminNagraManageOperationsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  operationsDoc: PropTypes.shape({
    campaignUuid: PropTypes.string,
    collectionId: PropTypes.string,
    asset: PropTypes.shape({
      title: PropTypes.string,
      assetUuid: PropTypes.string,
      monitoringStartTs: PropTypes.string,
      monitoringEndTs: PropTypes.string,
    }),
    monitoringOperations: PropTypes.arrayOf(
      PropTypes.shape({
        monitoringUuid: PropTypes.string.isRequired,
        campaignUuid: PropTypes.string,
        label: PropTypes.string,
        crawlingEndTs: PropTypes.string,
        status: PropTypes.string,
        createdAt: PropTypes.string,
      }),
    ),
  }),
  loading: PropTypes.bool.isRequired,
  creating: PropTypes.bool.isRequired,
  ending: PropTypes.string,
  fetchOperations: PropTypes.func.isRequired,
  createOperation: PropTypes.func.isRequired,
  endOperation: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    operationsDoc: state.nagraMonitoring.operationsDoc,
    loading: state.nagraMonitoring.loading,
    creating: state.nagraMonitoring.creating,
    ending: state.nagraMonitoring.ending,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchOperations: fetchNagraMonitoringOperations,
      createOperation: createNagraMonitoringOperation,
      endOperation: endNagraMonitoringOperation,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminNagraManageOperationsPage);
